import Backbone from 'backbone';
import _ from 'underscore';
import $ from 'jquery';
import Promise from 'bluebird';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { AudienceMeter } from './audience-meter';
import template from './template.pug';
import { unitValueMap } from './utils';

/**
 * ReachEstimateControl class
 * Updates the Reach Estimate for AdGroup Targets
 *
 * @class AudienceMeter
 */
export class ReachEstimateControl extends Backbone.View {
  initialize(options = {}) {
    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    const permissionConfig = DependencyResolver.get('component/config/index');
    this.permissions = permissionConfig.get('Permissions');

    // Allow Updating Estimates no more than once every 2000 ms
    this.debouncedUpdateEstimateFunc = _.debounce((data, hasAudience) => this.updateEstimateInternal(data, hasAudience), 2000); // eslint-disable-line max-len

    this.audienceMeter = new AudienceMeter();

    // Empty Init Promises for early validate() case
    this.postAudienceEstimation = Promise.resolve();
    this.postPrivacyCheck = Promise.resolve();

    // Set initial state to be valid
    this.isValid = true;
    this.lastEstimateData = null;

    // Panel is to be sticky, so it is always visible to user
    this.isPanelSticky = options.isSticky;
    this.viewContext = options.viewContext;
    this.scenario = options.scenario;
    this.customModifyTarget = options.modifyTarget;
    this.customThreshold = options.threshold;
    this.customRootMargin = options.rootMargin;

    $(window).on('resize', () => {
      this.updateEstimatesOnMeter(this.lastEstimateData);
    });
  }

  /**
   * Renders the view
   *
   * @returns {View} returns the view itself
   */
  render() {
    this.$el.html(template(this));

    this.$('.audience-meter-container').append(this.audienceMeter.render().el);

    if (this.isPanelSticky) {
      this.stickPanel();
    }

    // hide the view by default, since we only want to show it when there is data
    this.$el.hide();

    return this;
  }

  /**
   * Call debounced update estimate function
   *
   * @param {object} targetsData the targets
   * @param {bool} hasAudienceRemarketingList hasAudienceRemarketingList
   * @returns {Promise} - promise with status
   */
  updateEstimate(targetsData, hasAudienceRemarketingList) {
    return this.debouncedUpdateEstimateFunc(targetsData, hasAudienceRemarketingList);
  }

  /**
   * Makes the Reach control panel sticky
   *
   * @returns {undefined}
   */
  stickPanel() {
    if (!window.IntersectionObserver) {
      return;
    }

    const modifyTarget = (entries) => {
      // Sticky only when it intersects with the window at the top
      // and only when the control is displayed
      if (_.first(entries).intersectionRatio < 1 &&
        _.first(entries).boundingClientRect.height > 0 &&
        (_.first(entries).intersectionRect.y < 100 ||
          _.first(entries).intersectionRect.top < 100)) {
        this.$('.reach-estimate-control').addClass('sticky');
      } else {
        this.$('.reach-estimate-control').removeClass('sticky');
      }
    };

    const target = this.$('#reach-control-sentinel').get(0);

    this.observer = new IntersectionObserver(this.customModifyTarget || modifyTarget, {
      root: null,
      rootMargin: this.customRootMargin || '0px',
      threshold: this.customThreshold || 1.0,
    });
    this.observer.observe(target);
  }

  /**
   * Validate Audience Reach Estimate
   *
   * @returns {bool} returns true if reach is valid
   */
  validate() {
    return Promise.all([this.postAudienceEstimation, this.postPrivacyCheck])
      .then(() => this.permissions.IsAmplifyEnabled || this.isValid).catch(() => { // eslint-disable-line arrow-body-style
        // TODO: log response
        return true;
      });
  }

  /**
   * Returns the error message of the reach estimate control
   *
   * @returns {string} - string for error message
   */
  get errorMessage() {
    return this.errMsg;
  }

  /**
   * Update the Reach Estimates
   * Use the debounce version of this function when dealing with high volume inputs: updateEstimate
   *
   * @private
   * @param {object} targetsData the targets
   * @param {bool} hasAudienceRemarketingList hasAudienceRemarketingList
   * @returns {Promise} promise with status
   */
  updateEstimateInternal(targetsData, hasAudienceRemarketingList) {
    const odata = DependencyResolver.get('component/http/channel/odata');
    const context = DependencyResolver.get('audienceCampaignPageContext');

    // Update if current targets have Audience Remarketing lists
    // (used to display appropriate messaging if PrivacyCheck fails)
    this.hasAudienceRemarketingList = hasAudienceRemarketingList;

    const urlBase = `Customers(${context.CurrentCustomerId})/Accounts(${context.CurrentAccount.Id})/`;
    const audienceEstimationUrl = `${urlBase}Default.AudienceEstimationV2`;
    const privacyCheckUrl = `${urlBase}Default.PrivacyCheckV3`;
    const option = {
      host: 'adinsight.odata.url',
      path: 'AdInsight/V2/',
      data: { request: targetsData },
      customHeader: {
        name: 'x-ms-scenarioname',
        value: this.scenario || 'AudienceCampaign',
      },
    };

    // Create AudienceEstimation Promise
    if (!_.isEmpty(targetsData)) {
      this.postAudienceEstimation = odata.post(audienceEstimationUrl, option);
      this.postPrivacyCheck = odata.post(privacyCheckUrl, option);
    } else {
      this.postAudienceEstimation = Promise.resolve({});
      this.postPrivacyCheck = Promise.resolve({});
    }

    return Promise.all([this.postAudienceEstimation, this.postPrivacyCheck])
      .then(([estimateData, privacyData]) => {
        this.lastEstimateData = estimateData;

        if (_.get(estimateData, ['EstImpression', 'Value']) != null) {
          this.$el.show();
        } else {
          this.$el.hide();
        }
        this.updatePanel(estimateData, privacyData, estimateData && privacyData);
      })
      .catch((response) => {
        this.updatePanel(response, response, false);
      })
      .finally(withUiBlock(this.$el, this.i18n.getString('Loading'), false));
  }

  /**
   * Update Panel
   * @private
   *
   * @param {object} estimateData data with estimates
   * @param {object} privacyData data with privacy check results
   * @param {object} reqStatus indicates if request succeeded
   * @returns {undefined}
   */
  updatePanel(estimateData, privacyData, reqStatus) {
    const isReachTooSpecific = _.result(estimateData, 'IsImpressionTooSpecific');
    const privacyCheckResult = _.result(privacyData, 'CheckResult');

    // Update Estimate section of Reach Panel
    if (!reqStatus || privacyCheckResult !== 'Pass') {
      this.hideEstimate();
    } else {
      this.audienceMeter.show();
      this.$('.estimate-note').show();
      this.updateEstimatesOnMeter(estimateData);
    }

    // Update Warning section of Reach Panel
    this.errMsg = this.validateReach(privacyCheckResult, isReachTooSpecific, reqStatus);
    if ((privacyCheckResult !== 'Pass' || isReachTooSpecific || !reqStatus) && this.errMsg !== '') {
      this.showWarning(this.errMsg);
    } else {
      this.hideWarning();
    }
  }

  /**
   * Update Estimate
   * @private
   *
   * @param {object} data on estimates
   * @returns {undefined}
   */
  updateEstimatesOnMeter(data) {
    if (!data || !data.EstImpression) {
      return;
    }
    if (this.viewContext) {
      this.viewContext.SuggestedBid = data.SuggestedBid;
    }

    const estImprVal = data.EstImpression.Value;
    const estImprUnit = unitValueMap[data.EstImpression.Unit].unit;
    const estImprUnitVal = unitValueMap[data.EstImpression.Unit].value;

    const estImprText = `${estImprVal}${estImprUnit}`;
    const estImpr = estImprVal * estImprUnitVal;

    // Set new estimated impressions and move needle accordingly
    this.$('strong.impr').text(estImprText);

    if (data.IsImpressionTooSpecific) {
      this.audienceMeter.moveToSpecific();
    } else {
      this.audienceMeter.moveAlongBroad(this.calcMeterValue(estImpr));
    }
  }

  /**
   * Hide estimate
   * @private
   *
   * @returns {undefined}
   */
  hideEstimate() {
    this.audienceMeter.hide();
    this.$('.estimate-note').hide();
  }

  /**
   * Show warning
   * @private
   *
   * @param {String} erroMessage to show
   * @returns {undefined}
   */
  showWarning(erroMessage) {
    this.$('.reach-warning').show();
    this.$('.reach-warning').text(erroMessage);
  }

  /**
   * Hide warning
   * @private
   *
   * @returns {undefined}
   */
  hideWarning() {
    this.$('.reach-warning').hide();
  }

  /**
   * Checks for and sets Entity Valid state, also returns Warning Message if invalid
   * @private
   *
   * @param {object} privacyCheckResult The Privacy check Result
   * @param {object} isTooSpecific True if Estimated Impressions are too specific
   * @param {object} success indicates if request succeeded
   * @returns {undefined}
   */
  validateReach(privacyCheckResult, isTooSpecific, success) {
    let message = '';

    // Set valid state to true unless invalid state detected
    this.isValid = true;

    if (!success || privacyCheckResult === 'Pending') {
      message = this.i18n.getString('Audience_Campaign_ReachControl_NotAvailable');

      return message;
    }

    if (privacyCheckResult === 'TooNarrow' && this.hasAudienceRemarketingList) {
      message = this.i18n.getString('Audience_Campaign_ReachControl_PrivacyCheckFailed_AudienceReMarketing');
      this.isValid = true;

      return message;
    }

    if (privacyCheckResult === 'TooNarrow' && !this.hasAudienceRemarketingList) {
      message = this.i18n.getString('Audience_Campaign_ReachControl_PrivacyCheckFailed');
      this.isValid = false;

      return message;
    }

    // as long as the privacy check passed, we don't care about the possible estimation
    if (isTooSpecific && !this.permissions.IsKPIEstimateForAudienceFeedBasedEnabled) {
      message = this.i18n.getString('Audience_Campaign_ReachControl_TargettingTooSpecific');
      this.isValid = false;

      return message;
    }

    return message;
  }

  /**
   * Calculate the percentage value to be used in Audience Meter
   * @private
   *
   * @param {Number} estImpr estimated impressions
   * @returns {Number} percentageVal
   */
  calcMeterValue(estImpr) {
    // Use logarithimc scale to represent impressions: y = a log x + b
    // 100 = a log 64B + b: (64B experimentally derived as max impressions value)
    // 20 = a log 1K + b: (1K experimentally derived as min impressions value)
    // TODO: Tune this as needed after inital selfhosting
    const transformedVal = (4.45078 * Math.log(estImpr)) - 10.74487;

    return transformedVal;
  }

  remove() {
    super.remove();

    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
